import { useEffect, useState } from "react";
import {
  ApiError,
  listSectors,
  readIntake,
  type IntakeDraft,
  type IntakeRevision,
  type Role,
  type Sector,
} from "../api/client";
import { IntakeForm } from "../cb/IntakeForm";
import { Callout } from "../components/Callout";

/**
 * Step 1 of an audit that already exists — the intake, read back or revised.
 *
 * Two screens behind one stop, and which one is not a preference. The certification body
 * that owns the record gets the form it typed, prefilled and revisable. Everyone else gets
 * the record.
 *
 * **The lead auditor's read is the point of this screen, not a consolation.** They are
 * asked at Step 2 to approve a duration derived from an effective personnel figure, and
 * until now the counts behind it were unreachable: the determination shows the band and the
 * basis sentence, and the four staff figures that produced them lived only in
 * `intake_submissions`. Approving a determination whose inputs cannot be inspected is not
 * oversight, and Cl. 9.1.2 is what makes that a regulatory point rather than a usability
 * one.
 *
 * **Read-only is drawn as a record, not as a greyed-out form.** Screen 22 is the precedent —
 * the same values, read back rather than typed. A form full of disabled inputs invites the
 * reader to look for the enable, and there is no enable to find.
 */
export function IntakeStep({
  auditId,
  role,
  onRevised,
  onBack,
}: {
  auditId: string;
  role: Role;
  /** The audit changed underneath the caller — assertion 8 moved the state. */
  onRevised: () => void;
  onBack: () => void;
}) {
  const [record, setRecord] = useState<IntakeRevision | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    readIntake(auditId)
      .then(setRecord)
      .catch((cause: unknown) =>
        setError(cause instanceof ApiError ? cause.message : "The intake could not be read."),
      );
  }, [auditId]);

  if (error) {
    return (
      <p className="problem" role="alert">
        {error}
      </p>
    );
  }
  if (record === null) return <p className="sub">Reading the intake&hellip;</p>;

  if (role === "cb" && record.revisable) {
    return (
      <IntakeForm
        key={`revision-${record.version}`}
        revision={{ auditId, version: record.version, intake: record.intake, onRevised }}
        onCancel={onBack}
        onCreated={onRevised}
      />
    );
  }

  return <IntakeRecord record={record} role={role} />;
}

/** The intake as a record — the six cards of screen 24, read back. */
function IntakeRecord({ record, role }: { record: IntakeRevision; role: Role }) {
  const [sectors, setSectors] = useState<Sector[]>([]);
  useEffect(() => {
    listSectors()
      .then(setSectors)
      .catch(() => undefined);
  }, []);

  const intake = record.intake;

  return (
    <>
      <div className="between" style={{ marginBottom: "14px" }}>
        <div>
          <div className="eyebrow">Step 1 · Intake</div>
          <h1>{value(intake.client_organisation)}</h1>
          <p className="sub">
            Submitted intake, version <span className="mono">{record.version}</span>
            {record.version > 1 ? " — revised since submission" : ""}. These are the figures
            the Step 2 determination was derived from.
          </p>
        </div>
      </div>

      {/*
        The reason is shown rather than the control silently omitted. Which reason it is
        differs by party and both are regulatory: one body's record of its own client, or an
        audit that has already been conducted.
      */}
      <Callout
        tone="info"
        title={
          role === "cb"
            ? "This intake can no longer be revised."
            : "The certification body maintains this record."
        }
      >
        {record.refusal ??
          "A lead auditor reads the intake and does not amend it. Where these figures are " +
            "wrong, the lever is the Step 2 approval — withhold it and say why, and the " +
            "certification body revises the intake."}
      </Callout>

      <div className="card">
        <h2>1 · Organisation details</h2>
        <dl className="kv">
          <dt>Organisation</dt>
          <dd>{value(intake.client_organisation)}</dd>
          <dt>Industry sector</dt>
          <dd>{sectorLabels(intake.sector_indexes, sectors)}</dd>
          <dt>Address</dt>
          <dd>{value(intake.client_address)}</dd>
          <dt>Primary contact</dt>
          <dd>
            {value(intake.contact_name)} · {value(intake.contact_email)}
          </dd>
        </dl>
      </div>

      <div className="card">
        <h2>2 · Management system scope</h2>
        <dl className="kv">
          <dt>Scope statement</dt>
          <dd>{value(intake.certification_scope)}</dd>
          <dt>Products, services</dt>
          <dd>{value(intake.products_services)}</dd>
          <dt>Exclusions</dt>
          <dd>{value(intake.exclusions)}</dd>
        </dl>
      </div>

      <div className="card">
        <h2>3 · Audit details</h2>
        <dl className="kv">
          <dt>Standards</dt>
          <dd>{(intake.standards ?? []).join(" · ") || "—"}</dd>
          <dt>Audit type</dt>
          <dd>Stage 1 — initial certification</dd>
          <dt>Delivery</dt>
          <dd>Remote — ICT</dd>
          <dt>Planned date</dt>
          <dd>{value(intake.planned_date)}</dd>
        </dl>
      </div>

      {/*
        The four counts this screen exists for. Contractors are shown only where they were
        collected — Cl. 1.9 counts them for ISO 45001 alone, and a "—" against a field that
        never applied reads as a figure somebody forgot to enter.
      */}
      <div className="card">
        <h2>4 · Organisation size</h2>
        <dl className="kv">
          <dt>Permanent</dt>
          <dd>
            <span className="mono">{value(intake.permanent_staff)}</span>
            <span className="sub"> full-time equivalents</span>
          </dd>
          <dt>Temporary</dt>
          <dd>
            <span className="mono">{value(intake.temporary_staff)}</span>
          </dd>
          <dt>Part-time</dt>
          <dd>
            <span className="mono">{value(intake.part_time_staff)}</span>
            <span className="sub"> weighted at 0.5</span>
          </dd>
          {intake.contractor_staff ? (
            <>
              <dt>Contractors</dt>
              <dd>
                <span className="mono">{intake.contractor_staff}</span>
                <span className="sub"> counted for ISO 45001 — IAF MD 5 Cl. 1.9</span>
              </dd>
            </>
          ) : null}
          <dt>Sites</dt>
          <dd>
            <span className="mono">{value(intake.site_count)}</span>
          </dd>
          <dt>Shift work</dt>
          <dd>{yesNo(intake.shift_work)}</dd>
          <dt>OH&amp;S risk sector</dt>
          <dd>{value(intake.oshms_sector)}</dd>
          <dt>Complexity</dt>
          <dd>
            {intake.complexity ? (
              <>
                {intake.complexity}
                {intake.complexity_justification ? (
                  <>
                    <br />
                    <span className="sub">{intake.complexity_justification}</span>
                  </>
                ) : null}
              </>
            ) : (
              <span className="sub">Sector default</span>
            )}
          </dd>
        </dl>
      </div>

      <div className="card">
        <h2>5 · Audit team</h2>
        <dl className="kv">
          <dt>Technical expert</dt>
          <dd>
            {intake.technical_expert_required
              ? value(intake.technical_expert)
              : "Not required"}
          </dd>
        </dl>
      </div>

      <div className="card">
        <h2>6 · Impartiality and consent</h2>
        <dl className="kv">
          <dt>Consultancy</dt>
          <dd>
            {intake.consultancy_declared
              ? "Declared — ISO/IEC 17021-1 Cl. 4.2, flagged for impartiality review"
              : "None declared"}
          </dd>
          <dt>ICT consent</dt>
          <dd>{yesNo(intake.ict_consent_cb)}</dd>
          <dt>Confidentiality</dt>
          <dd>{yesNo(intake.confidentiality_ack)}</dd>
        </dl>
      </div>
    </>
  );
}

function sectorLabels(indexes: readonly number[] | undefined, sectors: Sector[]): string {
  if (!indexes?.length) return "—";
  return indexes
    .map((index) => sectors.find((sector) => sector.index === index)?.label ?? `IAF ${index}`)
    .join(" · ");
}

function yesNo(value: boolean | null | undefined): string {
  return value === true ? "Yes" : value === false ? "No" : "—";
}

function value(text: string | null | undefined): string {
  return text && text.trim() ? text : "—";
}

export type { IntakeDraft };
